import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchBar from './SearchBar';
import { isLargeEnoughForVirtualScroll, VIRTUAL_SCROLL_MIN_LENGTH } from '../utils/responseBodyUtils';

type TokenType = 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'text';

interface Token {
  type: TokenType;
  text: string;
  start: number; // character offset in full json
}

interface MatchRange {
  start: number;
  end: number;
}

/** Max characters per virtual row so fixed line height does not clip into the next row. */
const MAX_VIRTUAL_LINE_CHARS = 1000;

/**
 * Build virtual-scroll rows and character offsets. Long single lines (e.g. base64 in JSON)
 * are split into multiple rows so each row fits one fixed-height slot.
 */
function buildVirtualScrollLines(json: string): { lines: string[]; lineStarts: number[] } {
  const naturalLines = json.split('\n');
  const useChunked = naturalLines.length <= 20 && json.length >= VIRTUAL_SCROLL_MIN_LENGTH;
  const CHUNK_SIZE = MAX_VIRTUAL_LINE_CHARS;

  if (useChunked) {
    const lines: string[] = [];
    const lineStarts: number[] = [0];
    for (let i = 0; i < json.length; i += CHUNK_SIZE) {
      lines.push(json.slice(i, i + CHUNK_SIZE));
      lineStarts.push(Math.min(i + CHUNK_SIZE, json.length));
    }
    return { lines, lineStarts };
  }

  const lines: string[] = [];
  const lineStarts: number[] = [0];
  let pos = 0;
  for (let li = 0; li < naturalLines.length; li++) {
    const raw = naturalLines[li];
    const lineStart = pos;
    if (raw.length <= CHUNK_SIZE) {
      lines.push(raw);
    } else {
      for (let i = 0; i < raw.length; i += CHUNK_SIZE) {
        if (i > 0) lineStarts.push(lineStart + i);
        lines.push(raw.slice(i, i + CHUNK_SIZE));
      }
    }
    pos = lineStart + raw.length;
    if (li < naturalLines.length - 1) pos += 1;
    lineStarts.push(pos);
  }
  return { lines, lineStarts };
}

/**
 * Lightweight JSON tokenizer for pretty-printed JSON. Single pass, synchronous.
 * Used so Pretty tab shows syntax highlighting immediately (no Monaco load/tokenize delay).
 */
function tokenizeJson(json: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = json.length;

  const peek = () => (i < n ? json[i] : '');
  const advance = () => (i < n ? json[i++] : '');
  // Only skip spaces and tabs so newlines are preserved (pretty-printed layout)
  const skipSpacesAndTabs = () => {
    while (i < n && (json[i] === ' ' || json[i] === '\t')) i++;
  };

  while (i < n) {
    skipSpacesAndTabs();
    if (i >= n) break;
    const ch = peek();
    if (ch === '"') {
      let text = advance();
      while (i < n) {
        const c = advance();
        text += c;
        if (c === '\\') {
          if (i < n) text += advance();
        } else if (c === '"') break;
      }
      let j = i;
      while (j < n && (json[j] === ' ' || json[j] === '\t' || json[j] === '\n' || json[j] === '\r')) j++;
      const isKey = j < n && json[j] === ':';
      const start = i - text.length;
      tokens.push({ type: isKey ? 'key' : 'string', text, start });
      continue;
    }
    if (/[-0-9]/.test(ch)) {
      const start = i;
      let text = advance();
      while (i < n && /[0-9.eE+]/.test(json[i])) text += advance();
      tokens.push({ type: 'number', text, start });
      continue;
    }
    if (ch === 't' && json.slice(i, i + 4) === 'true') {
      const start = i;
      i += 4;
      tokens.push({ type: 'boolean', text: 'true', start });
      continue;
    }
    if (ch === 'f' && json.slice(i, i + 5) === 'false') {
      const start = i;
      i += 5;
      tokens.push({ type: 'boolean', text: 'false', start });
      continue;
    }
    if (ch === 'n' && json.slice(i, i + 4) === 'null') {
      const start = i;
      i += 4;
      tokens.push({ type: 'null', text: 'null', start });
      continue;
    }
    if ('{}[]:,'.includes(ch)) {
      const start = i;
      tokens.push({ type: 'punctuation', text: advance(), start });
      continue;
    }
    const start = i;
    tokens.push({ type: 'text', text: advance(), start });
  }
  return tokens;
}

const TOKEN_COLORS: Record<TokenType, string> = {
  key: '#0451a1',
  string: '#a31515',
  number: '#098658',
  boolean: '#0000ff',
  null: '#0000ff',
  punctuation: '#000000',
  text: '#000000',
};

const HIGHLIGHT_BG = '#ffeb3b';
const HIGHLIGHT_BG_ACTIVE = '#ff9800';

const LINE_HEIGHT_PX = 21;
const VIRTUAL_OVERSCAN = 8;
/** Syntax highlighting a multi-MB payload (e.g. GitHub OpenAPI) freezes Chromium. */
const TOKENIZE_MAX_CHARS = 512 * 1024;
const MAX_SEARCH_MATCHES = 500;

function firstLineIndexForChar(lineStarts: number[], charPos: number): number {
  let lo = 0;
  let hi = lineStarts.length - 2;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lineStarts[mid] <= charPos) lo = mid + 1;
    else hi = mid - 1;
  }
  return Math.max(0, hi);
}

function findMatches(text: string, query: string, caseInsensitive: boolean, useRegex: boolean): MatchRange[] {
  if (!query.trim()) return [];
  const ranges: MatchRange[] = [];
  try {
    if (useRegex) {
      const flags = caseInsensitive ? 'gi' : 'g';
      const re = new RegExp(query, flags);
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        ranges.push({ start: m.index, end: m.index + m[0].length });
        if (ranges.length >= MAX_SEARCH_MATCHES) break;
      }
    } else {
      const search = caseInsensitive ? query.toLowerCase() : query;
      const haystack = caseInsensitive ? text.toLowerCase() : text;
      let pos = 0;
      while ((pos = haystack.indexOf(search, pos)) >= 0) {
        ranges.push({ start: pos, end: pos + search.length });
        if (ranges.length >= MAX_SEARCH_MATCHES) break;
        pos += 1;
      }
    }
  } catch {
    // invalid regex - no matches
  }
  return ranges;
}

interface JsonSyntaxHighlightProps {
  json: string;
  /** Max height for scroll container */
  maxHeight?: string | number;
  /** When provided, shows copy button before the search bar; copies this content */
  copyContent?: string;
  /** Tooltip / aria for the copy control (default: response body). */
  copyLabel?: string;
  /** When provided, Search bar is rendered in this external container (e.g. Status row) instead of above content */
  searchContainerRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Renders pretty-printed JSON with instant syntax highlighting (no Monaco).
 * Includes in-editor search with highlight and Next/Previous navigation.
 */
interface TokenSegment {
  token: Token;
  segStart: number;
  segEnd: number;
}

const JsonSyntaxHighlight: React.FC<JsonSyntaxHighlightProps> = ({
  json,
  maxHeight = '400px',
  copyContent,
  copyLabel = 'Copy response body',
  searchContainerRef,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(400);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    if (!copyContent) return;
    try {
      await navigator.clipboard.writeText(copyContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [useRegex, setUseRegex] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const contentRef = useRef<HTMLPreElement>(null);
  const matchRefs = useRef<Map<number, HTMLSpanElement>>(new Map());

  const skipHighlight = json.length > TOKENIZE_MAX_CHARS;
  const tokens = useMemo(() => (skipHighlight ? [] : tokenizeJson(json)), [json, skipHighlight]);

  const matches = useMemo(
    () => findMatches(json, searchQuery, caseInsensitive, useRegex),
    [json, searchQuery, caseInsensitive, useRegex]
  );

  const matchCount = matches.length;
  const useVirtual = skipHighlight || isLargeEnoughForVirtualScroll(json);

  const virtualScrollData = useMemo(() => {
    if (!useVirtual) return { lines: [] as string[], lineStarts: [] as number[], tokensPerLine: [] as TokenSegment[][] };
    const { lines, lineStarts } = buildVirtualScrollLines(json);
    const tokensPerLine: TokenSegment[][] = lines.map(() => []);
    if (tokens.length === 0) {
      return { lines, lineStarts, tokensPerLine };
    }
    tokens.forEach((t) => {
      const tEnd = t.start + t.text.length;
      let i = firstLineIndexForChar(lineStarts, t.start);
      while (i < lines.length && lineStarts[i] < tEnd) {
        const lineStart = lineStarts[i];
        const lineEndExcl = lineStarts[i + 1];
        if (t.start < lineEndExcl && tEnd > lineStart) {
          const segStart = Math.max(0, lineStart - t.start);
          const segEnd = Math.min(t.text.length, lineEndExcl - t.start);
          if (segStart < segEnd) {
            tokensPerLine[i].push({ token: t, segStart, segEnd });
          }
        }
        i++;
      }
    });
    return { lines, lineStarts, tokensPerLine };
  }, [json, tokens, useVirtual]);

  useEffect(() => {
    setCurrentMatchIndex((prev) => (matchCount > 0 ? Math.min(prev, matchCount - 1) : 0));
  }, [matchCount]);

  useEffect(() => {
    if (matchCount > 0) {
      const el = matchRefs.current.get(currentMatchIndex);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Ref may not be set yet; try after paint
        const id = requestAnimationFrame(() => {
          matchRefs.current.get(currentMatchIndex)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return () => cancelAnimationFrame(id);
      }
    }
  }, [currentMatchIndex, matchCount]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !useVirtual) return;
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setScrollTop(el.scrollTop);
        setContainerHeight(el.clientHeight);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    setContainerHeight(el.clientHeight);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [useVirtual]);

  const handleNext = () => {
    setCurrentMatchIndex((prev) => (prev + 1) % Math.max(1, matchCount));
  };

  const handlePrev = () => {
    setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % Math.max(1, matchCount));
  };

  const content = useMemo(() => {
    return tokens.map((t, tokenIdx) => {
      const tokenEnd = t.start + t.text.length;
      const overlapping = matches
        .map((m, i) => ({ ...m, matchIndex: i }))
        .filter((m) => m.start < tokenEnd && m.end > t.start)
        .sort((a, b) => a.start - b.start);

      if (overlapping.length === 0) {
        return (
          <span key={tokenIdx} style={{ color: TOKEN_COLORS[t.type] }}>
            {t.text}
          </span>
        );
      }

      const parts: React.ReactNode[] = [];
      let pos = 0;

      overlapping.forEach((m) => {
        const segStart = Math.max(pos, m.start - t.start);
        const segEnd = Math.min(t.text.length, m.end - t.start);
        if (segStart > pos) {
          parts.push(
            <span key={`${tokenIdx}-${pos}`} style={{ color: TOKEN_COLORS[t.type] }}>
              {t.text.slice(pos, segStart)}
            </span>
          );
        }
        const isActive = m.matchIndex === currentMatchIndex;
        const refCallback = (el: HTMLSpanElement | null) => {
          if (el && isActive) matchRefs.current.set(currentMatchIndex, el);
        };
        parts.push(
          <span
            key={`${tokenIdx}-m-${m.matchIndex}`}
            ref={refCallback}
            style={{
              backgroundColor: isActive ? HIGHLIGHT_BG_ACTIVE : HIGHLIGHT_BG,
              color: '#000',
              borderRadius: 1,
            }}
          >
            {t.text.slice(segStart, segEnd)}
          </span>
        );
        pos = segEnd;
      });
      if (pos < t.text.length) {
        parts.push(
          <span key={`${tokenIdx}-${pos}`} style={{ color: TOKEN_COLORS[t.type] }}>
            {t.text.slice(pos)}
          </span>
        );
      }
      return <span key={tokenIdx}>{parts}</span>;
    });
  }, [tokens, matches, currentMatchIndex]);

  const { lines: virtualLines, lineStarts, tokensPerLine } = virtualScrollData;
  const totalVirtualHeight = virtualLines.length * LINE_HEIGHT_PX;
  const startLine = useVirtual
    ? Math.max(0, Math.floor(scrollTop / LINE_HEIGHT_PX) - VIRTUAL_OVERSCAN)
    : 0;
  const endLine = useVirtual
    ? Math.min(virtualLines.length - 1, Math.ceil((scrollTop + containerHeight) / LINE_HEIGHT_PX) + VIRTUAL_OVERSCAN)
    : 0;

  const virtualVisibleContent = useMemo(() => {
    if (!useVirtual || startLine > endLine) return null;
    const result: React.ReactNode[] = [];
    for (let lineIdx = startLine; lineIdx <= endLine; lineIdx++) {
      const segments = tokensPerLine[lineIdx] || [];
      const lineParts: React.ReactNode[] = [];
      if (segments.length === 0) {
        const lineText = virtualLines[lineIdx] || '';
        result.push(
          <div
            key={lineIdx}
            style={{
              position: 'absolute',
              left: 0,
              top: lineIdx * LINE_HEIGHT_PX,
              height: LINE_HEIGHT_PX,
              lineHeight: `${LINE_HEIGHT_PX}px`,
              paddingLeft: 16,
              paddingRight: 16,
              whiteSpace: 'pre',
              overflow: 'hidden',
              fontFamily: 'Fira Mono, monospace',
              fontSize: 14,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {lineText || '\u00A0'}
          </div>
        );
        continue;
      }
      segments.forEach(({ token: t, segStart, segEnd }, segIdx) => {
        const segText = t.text.slice(segStart, segEnd);
        const segStartChar = t.start + segStart;
        const segEndChar = t.start + segEnd;
        const overlapping = matches
          .map((m, i) => ({ ...m, matchIndex: i }))
          .filter((m) => m.start < segEndChar && m.end > segStartChar)
          .sort((a, b) => a.start - b.start);
        if (overlapping.length === 0) {
          lineParts.push(
            <span key={segIdx} style={{ color: TOKEN_COLORS[t.type] }}>
              {segText}
            </span>
          );
          return;
        }
        let pos = 0;
        overlapping.forEach((m) => {
          const sStart = Math.max(pos, m.start - segStartChar);
          const sEnd = Math.min(segText.length, m.end - segStartChar);
          if (sStart > pos) {
            lineParts.push(
              <span key={`${segIdx}-${pos}`} style={{ color: TOKEN_COLORS[t.type] }}>
                {segText.slice(pos, sStart)}
              </span>
            );
          }
          const isActive = m.matchIndex === currentMatchIndex;
          const refCallback = (el: HTMLSpanElement | null) => {
            if (el && isActive) matchRefs.current.set(currentMatchIndex, el);
          };
          lineParts.push(
            <span
              key={`${segIdx}-m-${m.matchIndex}`}
              ref={refCallback}
              style={{
                backgroundColor: isActive ? HIGHLIGHT_BG_ACTIVE : HIGHLIGHT_BG,
                color: '#000',
                borderRadius: 1,
              }}
            >
              {segText.slice(sStart, sEnd)}
            </span>
          );
          pos = sEnd;
        });
        if (pos < segText.length) {
          lineParts.push(
            <span key={`${segIdx}-${pos}`} style={{ color: TOKEN_COLORS[t.type] }}>
              {segText.slice(pos)}
            </span>
          );
        }
      });
      result.push(
        <div
          key={lineIdx}
          style={{
            position: 'absolute',
            left: 0,
            top: lineIdx * LINE_HEIGHT_PX,
            height: LINE_HEIGHT_PX,
            lineHeight: `${LINE_HEIGHT_PX}px`,
            paddingLeft: 16,
            paddingRight: 16,
            whiteSpace: 'pre',
            overflow: 'hidden',
            fontFamily: 'Fira Mono, monospace',
            fontSize: 14,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {lineParts.length > 0 ? lineParts : '\u00A0'}
        </div>
      );
    }
    return result;
  }, [useVirtual, startLine, endLine, tokensPerLine, lineStarts, virtualLines, matches, currentMatchIndex]);

  const copyButton =
    copyContent != null ? (
      <Tooltip title={copied ? 'Copied!' : copyLabel}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
          aria-label={copyLabel}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  const searchBarEl = (
    <SearchBar
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      caseInsensitive={caseInsensitive}
      onCaseInsensitiveChange={setCaseInsensitive}
      useRegex={useRegex}
      onUseRegexChange={setUseRegex}
      matchCount={matchCount}
      currentMatchIndex={currentMatchIndex}
      onNext={handleNext}
      onPrev={handlePrev}
      compact
      overlay={!!searchContainerRef}
      inOverlayGroup={!!searchContainerRef}
      prefix={copyButton}
    />
  );

  useEffect(() => {
    if (!json.trim() && searchQuery) setSearchQuery('');
  }, [json, searchQuery]);

  const [headerPortalContainer, setHeaderPortalContainer] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!searchContainerRef) {
      setHeaderPortalContainer(null);
      return;
    }
    const el = searchContainerRef.current;
    if (el) {
      setHeaderPortalContainer(el);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      setHeaderPortalContainer(searchContainerRef.current);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
      setHeaderPortalContainer(null);
    };
  }, [searchContainerRef]);

  const headerPortal =
    searchContainerRef && headerPortalContainer ? createPortal(searchBarEl, headerPortalContainer) : null;

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
      {headerPortal}
      {!searchContainerRef && (
        <Box
          data-testid="json-syntax-toolbar"
          sx={{
            flexShrink: 0,
            minWidth: 0,
            width: '100%',
            px: 1,
            boxSizing: 'border-box',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            caseInsensitive={caseInsensitive}
            onCaseInsensitiveChange={setCaseInsensitive}
            useRegex={useRegex}
            onUseRegexChange={setUseRegex}
            matchCount={matchCount}
            currentMatchIndex={currentMatchIndex}
            onNext={handleNext}
            onPrev={handlePrev}
            compact
            prefix={copyButton}
          />
        </Box>
      )}
      {useVirtual ? (
        <Box
          ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
          component="pre"
          sx={{
            p: 0,
            m: 0,
            minHeight: 80,
            maxHeight,
            overflow: 'auto',
            fontFamily: 'Fira Mono, monospace',
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: 'pre',
            wordBreak: 'normal',
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'relative', width: '100%', height: totalVirtualHeight }}>
            {virtualVisibleContent}
          </Box>
        </Box>
      ) : (
        <Box
          ref={contentRef}
          component="pre"
          sx={{
            p: 2,
            m: 0,
            minHeight: 80,
            maxHeight,
            overflow: 'auto',
            fontFamily: 'Fira Mono, monospace',
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: 'pre',
            wordBreak: 'normal',
          }}
        >
          {content}
        </Box>
      )}
    </Box>
  );
};

export default JsonSyntaxHighlight;
